/**
 * Shared flex scroll regions (use inside a column with flex: 1 and minHeight: 0).
 * Parent clips height; child scrolls. Headers/footers stay flexShrink: 0 siblings.
 *
 * Use scrollRegionNestedSx only inside a padded Paper/card so the scrollbar aligns
 * with the panel edge. Use scrollRegionSx for card lists; pad tables with tableContainerInsetSx.
 */

import { mobileContentInsetSx } from "utils/tableInset";

const MOBILE_MAX = 899.95;
const DESKTOP_MIN = 900;

export const scrollRegionCoreSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
  // Reserve space for the thumb on the scrolling edge only (no extra side padding).
  scrollbarGutter: "stable",
};

/** Reset nested breakout on desktop. */
const scrollRegionNestedDesktopResetSx = {
  [`@media (min-width:${DESKTOP_MIN}px)`]: {
    width: "auto",
    maxWidth: "none",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
};

/**
 * Scroll region inside a padded parent (e.g. Paper with p:1).
 * Breaks out of parent horizontal padding on mobile so content and scrollbar use full panel width.
 * @param {number} spacingUnits MUI spacing units to break out (1 = 8px).
 */
export function scrollRegionNestedSx(spacingUnits = 1) {
  const insetPx = spacingUnits * 8;
  return {
    ...scrollRegionCoreSx,
    [`@media (max-width:${MOBILE_MAX}px)`]: {
      width: `calc(100% + ${insetPx * 2}px)`,
      maxWidth: `calc(100% + ${insetPx * 2}px)`,
      marginLeft: `-${insetPx}px`,
      marginRight: `-${insetPx}px`,
      boxSizing: "border-box",
    },
    ...scrollRegionNestedDesktopResetSx,
  };
}

/** Default scroll region — full width of parent with mobile content inset. */
export const scrollRegionSx = {
  ...scrollRegionCoreSx,
  ...mobileContentInsetSx,
};

/** Clips height and passes it to a scrollRegion child. */
export const scrollRegionParentSx = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

/** Full-height page shell (outlet child, form screen, inventory tab). */
export const flexFillColumnSx = {
  flex: 1,
  minHeight: 0,
  width: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
